import React, { useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { StudentAppIcons, StudentAppsHost, useStudentApps } from './StudentAppSwitcher';
import LegalLinks from './legal/LegalLinks';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';
import { loginPathFor } from '../utils/returnTo';

interface DashboardLayoutProps {
  requiredRole: UserRole;
}

/**
 * The row above the top bar: the menu button on a phone, and, for a student, the app icons where the
 * top bar has no room for them (StudentAppSwitcher.tsx says where that is). Anyone else, and a student
 * whose icons sit in the top bar, gets the menu button alone, as before.
 */
const MenuRow: React.FC<{ onMenu: () => void }> = ({ onMenu }) => {
  const switcher = useStudentApps();
  const icons = switcher !== null && switcher.apps.length > 0 && !switcher.wide;
  const button = (
    <button onClick={onMenu} className={`lg:hidden p-1.5 rounded-[10px] bg-[#fafafa] hover:bg-gray-100 ${icons ? 'flex-1' : 'mb-3'}`}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#2b2b2b">
        <path d="M3 6h18v2H3zM3 11h18v2H3zM3 16h18v2H3z"/>
      </svg>
    </button>
  );
  if (!icons) return button;
  return (
    <div className="mb-3 flex items-center justify-end gap-3">
      {button}
      <StudentAppIcons site="strip" />
    </div>
  );
};

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ requiredRole }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // A signed-out visitor is sent to sign in and then BACK to the page they asked for: the
  // destination rides along as /login?next=… (utils/returnTo.ts decides what may be one).
  if (!user) return <Navigate to={loginPathFor(location.pathname, location.search, requiredRole)} replace />;
  if (user.role !== requiredRole) return <Navigate to={`/${user.role}`} replace />;

  const shell = (
    <div className="min-h-screen bg-[#f6f6f6]">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 z-40 h-full transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main content — proper spacing from edges.
          Yapışkan altbilgi: sayfa içeriği ekranı doldurmadığında altbilgi
          içeriğin hemen altında boşlukta asılı kalmasın, ekranın altına
          insin. `grow` (flex-basis'e dokunmayan flex-grow) bilerek `flex-1`
          yerine kullanıldı — içerik ekrandan uzunsa `flex-basis: 0` bazı
          tarayıcılarda taşmayı yanlış hesaplıyor. */}
      <div className="lg:ml-[240px] xl:ml-[280px] min-h-screen flex flex-col">
        <div className="px-6 xl:px-10 pt-4 xl:pt-5 grow flex flex-col">
          {/* Mobile hamburger, and the student's app icons where the top bar has no room for them */}
          <MenuRow onMenu={() => setSidebarOpen(true)} />
          <TopBar />
          <div className="grow">
            <Outlet />
          </div>

          {/* Aydınlatma metni ve çerez tercihleri, uygulamanın her sayfasından
              bir tık uzakta olmalı — profil sayfasına gömülü tek bir bağlantı
              "kolay erişilebilir" sayılmaz. */}
          <footer className="mt-10 border-t border-[#e9e9e9] pt-5 pb-4">
            <LegalLinks />
          </footer>
        </div>
      </div>
    </div>
  );

  // A student's switcher (the app icons and the screen that opens another app) lives around the whole
  // shell, so that the screen can cover it and make it inert while the browser leaves.
  return user.role === 'student' ? <StudentAppsHost>{shell}</StudentAppsHost> : shell;
};

export default DashboardLayout;
